// Duplique volontairement les chemins déclarés dans App.js (React.lazy) : sert à
// précharger, AVANT l'hydration, le composant de la route déjà présente dans le
// HTML pré-rendu (scripts/generate-route-previews.mjs), et à le rendre disponible
// de façon SYNCHRONE (registre resolvedComponents) plutôt que via React.lazy().
//
// Pourquoi ne pas simplement précharger le chunk et laisser React.lazy() s'en
// charger : une Promise ne se résout jamais de façon synchrone, même déjà en
// cache — donc React.lazy() suspend systématiquement au tout premier rendu et
// affiche son fallback (null), qui ne correspond pas au HTML déjà présent →
// mismatch d'hydration (erreur #418). En rendant le composant réel directement
// (sans passer par lazy()) pour ce premier rendu, on évite complètement la
// frontière <Suspense> pour la route déjà pré-rendue.
//
// Si une route est ajoutée dans App.js sans être répercutée ici, elle retombe
// simplement sur React.lazy() (comportement précédent) — pas d'aggravation.
const ROUTES = [
  ['/', () => import('./pages/HomePage')],
  ['/etudes-de-cas', () => import('./pages/ClientsPage/ClientsPage')],
  ['/etudes-de-cas/:slug', () => import('./pages/CaseStudyPage/CaseStudyPage')],
  ['/products/agents', () => import('./pages/AgentsPage/AgentsPage')],
  ['/products/workflows', () => import('./pages/WorkflowsPage/WorkflowsPage')],
  ['/products/documents-ai', () => import('./pages/DocumentsAIPage/DocumentsAIPage')],
  ['/products/models', () => import('./pages/ModelsPage/ModelsPage')],
  ['/products/contact-center', () => import('./pages/ContactCenterPage/ContactCenterPage')],
  ['/solutions/financial-services', () => import('./pages/FinancialServicesPage/FinancialServicesPage')],
  ['/solutions/public-government', () => import('./pages/PublicGovernmentPage/PublicGovernmentPage')],
  ['/solutions/finance', () => import('./pages/SmartlyFinanceCenter/SmartlyFinanceCenter')],
  ['/about-us', () => import('./pages/AboutPage/AboutPage')],
  ['/jobs', () => import('./pages/JobsPage/JobsPage')],
  ['/partners', () => import('./pages/PartnersPage/PartnersPage')],
  ['/partners/:slug', () => import('./pages/PartnerDetailPage/PartnerDetailPage')],
  ['/blog', () => import('./pages/BlogPage/BlogPage')],
  ['/blog/:slug', () => import('./pages/BlogPostPage/BlogPostPage')],
  ['/legal-docs', () => import('./pages/LegalDocsPage/LegalDocsPage')],
  ['/legal-docs/:slug', () => import('./pages/LegalDocsPage/LegalDocumentPage')],
  ['/code-of-conduct', () => import('./pages/CodeOfConductPage/CodeOfConductPage')],
  ['/connexion', () => import('./pages/LoginGatewayPage/LoginGatewayPage')],
  ['/login', () => import('./pages/LoginGatewayPage/LoginGatewayPage')],
  ['/documentation', () => import('./pages/DocumentationGatewayPage/DocumentationGatewayPage')],
  ['/clients-demos/air-tahiti', () => import('./pages/AirTahitiDemoPage/AirTahitiDemoPage')],
  ['/100-pratique', () => import('./pages/HundredPratiquePage/HundredPratiquePage')],
  ['/100-pratique-utilisateurs-metier', () => import('./pages/HundredPratiqueUtilisateursMetierPage/HundredPratiqueUtilisateursMetierPage')],
  ['/100-pratique-equipe-projet', () => import('./pages/HundredPratiqueEquipeProjetPage/HundredPratiqueEquipeProjetPage')],
];

function pathToRegex(pattern) {
  const escaped = pattern
    .split('/')
    .map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('/');
  return new RegExp(`^${escaped}/?$`);
}

const COMPILED = ROUTES.map(([pattern, loader]) => [pattern, pathToRegex(pattern), loader]);

// path pattern (ex: '/blog/:slug') -> composant déjà résolu (synchrone)
export const resolvedComponents = {};

export function preloadRouteChunk(pathname) {
  const normalized = pathname || '/';
  const match = COMPILED.find(([, regex]) => regex.test(normalized));
  if (!match) return Promise.resolve();
  const [pattern, , loader] = match;
  return loader()
    .then((mod) => { resolvedComponents[pattern] = mod.default; })
    .catch(() => {});
}
